import React, { useEffect, useMemo, useRef, useState } from 'react';
import PageView from '../common/pageView';
import RegistrationModal from '../betFeed/registrationModal';
import SmallLoadingAlt from '../global/smallLoadingAlt';
import SessionService from '../../service/sessionService';
import BetCartService from '../../service/betCartService';
import AccessService from '../../service/accessService';
import eventBus from '../../service/eventBus';
import { getBetRecommendations } from '../../service/propsViewerService';
import { EVENT_BET_CART_UPDATED, LOGGED_OUT_VISIBLE_CARD_LIMIT } from '../../utils/constants';
import componentStyles from './propsViewer.module.css';
import globalStyles from '../globals.module.css';
import { FaCheck, FaChevronDown, FaChevronRight, FaFilter, FaPlus, FaTimes } from 'react-icons/fa';

const PICK_MODES = [
    { key: 'all', label: 'All' },
    { key: 'game', label: 'Game Bets', betTypes: ['moneyline', 'spread', 'total'] },
    { key: 'props', label: 'Props', betTypes: ['prop'] }
];

const GAME_BET_TYPE_ORDER = {
    moneyline: 1,
    spread: 2,
    total: 3
};

const formatEventTime = (dateTime) => {
    if (!dateTime) return 'TBD';

    try {
        return new Date(dateTime).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    } catch (error) {
        return 'TBD';
    }
};

const formatBetType = (betType) => {
    switch (betType) {
        case 'moneyline':
            return 'Moneyline';
        case 'spread':
            return 'Spread';
        case 'total':
            return 'Total';
        case 'prop':
            return 'Prop';
        default:
            return betType || 'Pick';
    }
};

const normalizeFilterValue = (value) => String(value || '').trim();

const stripOddsFromLabel = (label) => {
    const text = String(label || '').trim();
    const match = text.match(/^(.*?)\s*\(([^)]*(?:\/|\+|-)[^)]*)\)\s*$/);

    if (!match) {
        return {
            pickText: text,
            oddsText: ''
        };
    }

    return {
        pickText: match[1].trim(),
        oddsText: match[2].trim()
    };
};

const getBetConfidence = (bet) => (
    (() => {
        const confidence = (
            bet.confidence ||
            bet.betConfidence ||
            bet.recommendationConfidence ||
            bet.recommendation_level ||
            bet.recommendationLevel ||
            ''
        );

        if (confidence === 3 || confidence === '3') return 'HIGH';
        if (confidence === 2 || confidence === '2') return 'MEDIUM';
        if (confidence === 1 || confidence === '1') return 'LOW';
        return confidence;
    })()
);

const getConfidenceClass = (confidence) => {
    switch (String(confidence || '').toLowerCase()) {
        case 'high':
            return componentStyles.confidenceHigh;
        case 'medium':
            return componentStyles.confidenceMedium;
        case 'low':
            return componentStyles.confidenceLow;
        default:
            return componentStyles.confidenceUnknown;
    }
};

const getConfidenceLabel = (confidence) => {
    const label = String(confidence || '').trim();
    return label ? label.toUpperCase() : 'UNKNOWN';
};

const getRankLabel = (bet, fallbackIndex) => {
    const rank = bet.rank || bet.recommendationRank || bet.order || fallbackIndex;
    return rank ? `#${rank}` : '';
};

const getExplanationText = (explanation) => {
    if (Array.isArray(explanation)) {
        return explanation.filter(Boolean).join(' ');
    }

    return String(explanation || '').trim();
};

const buildCartBetFromRecommendation = (bet, event) => {
    const label = bet.label || bet.recommendedBet || 'Recommended pick';
    const eventName = event.eventName || `${event.awayTeam || ''} @ ${event.homeTeam || ''}`.trim() || 'Event TBD';

    return {
        ...bet,
        sourceBetId: bet.id,
        cartKey: bet.id,
        type: formatBetType(bet.betType),
        sport: bet.sport || event.sport || '',
        event: eventName,
        eventName,
        eventId: bet.eventId || event.eventId || '',
        oddsApiEventId: bet.eventId || event.eventId || '',
        scoutingReportId: bet.eventId || event.eventId || '',
        date: event.dateTime || '',
        bet: label,
        label,
        recommendedBet: bet.recommendedBet || label,
        player: bet.player || '',
        side: bet.side || ''
    };
};

const PickCard = ({ bet, event, displayIndex, isExpanded, onToggle, isAuthenticated, isSaved, onToggleSaved }) => {
    const cartBet = buildCartBetFromRecommendation(bet, event);
    const explanation = getExplanationText(bet.explanation);
    const eventName = event.eventName || `${event.awayTeam || ''} @ ${event.homeTeam || ''}`.trim() || 'Event TBD';
    const confidence = getBetConfidence(bet);
    const rawLabel = bet.betType === 'prop'
        ? bet.recommendedBet || bet.label || 'Recommended pick'
        : bet.label || bet.recommendedBet || 'Recommended pick';
    const { pickText, oddsText } = stripOddsFromLabel(rawLabel);
    const contextLabel = bet.player || bet.side || '';
    const marketLabel = bet.marketDisplayName || formatBetType(bet.betType);
    const isProp = bet.betType === 'prop';
    const displayPickText = isProp && contextLabel
        ? `${contextLabel} ${pickText}`
        : pickText;
    const rankLabel = getRankLabel(bet, displayIndex);
    const canAddToCart = Boolean(cartBet.bet && cartBet.event && cartBet.eventId);

    return (
        <div className={componentStyles.card}>
            <div className={componentStyles.cardHeader}>
                <div>
                    <div className={componentStyles.pickTitleRow}>
                        <p className={componentStyles.pickText}>{displayPickText}</p>
                        {oddsText && <span className={componentStyles.oddsBadge}>{oddsText}</span>}
                    </div>
                    <div className={componentStyles.playerText}>
                        {isProp ? marketLabel : formatBetType(bet.betType)}
                    </div>
                </div>
                <div className={componentStyles.cardActions}>
                    {confidence && (
                        <span className={`${componentStyles.confidenceBadge} ${getConfidenceClass(confidence)}`}>
                            {getConfidenceLabel(confidence)}
                        </span>
                    )}
                    {isAuthenticated && (
                        <button
                            type="button"
                            className={`${componentStyles.cartToggleButton} ${isSaved ? componentStyles.cartToggleButtonSaved : ''}`}
                            onClick={() => onToggleSaved(cartBet)}
                            disabled={!canAddToCart}
                            title={isSaved ? 'Remove from bet cart' : 'Add to bet cart'}
                        >
                            {isSaved ? <FaCheck /> : <FaPlus />}
                        </button>
                    )}
                </div>
            </div>

            <div className={componentStyles.metaLine}>
                <span>{eventName}</span>
                <span>{formatEventTime(event.dateTime)}</span>
                {rankLabel && <span>{rankLabel}</span>}
            </div>

            {explanation && (
                <>
                    <button type="button" className={componentStyles.detailsButton} onClick={onToggle}>
                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                        Details
                    </button>
                    {isExpanded && (
                        <div className={componentStyles.explanation}>
                            {explanation}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

const PropsViewer = () => {
    const sessionService = SessionService.getInstance();
    const betCartService = BetCartService.getInstance();
    const accessService = AccessService.getInstance();
    const canUsePicksFilters = accessService.isProOrAdmin();
    const [isAuthenticated] = useState(sessionService.isAuthenticated());
    const [events, setEvents] = useState([]);
    const [optionEvents, setOptionEvents] = useState([]);
    const [totalBets, setTotalBets] = useState(0);
    const [accessMeta, setAccessMeta] = useState(null);
    const [knownSports, setKnownSports] = useState(() => {
        const saved = sessionService.getLocalSetting('picksViewer_knownSports', []);
        return Array.isArray(saved) ? saved : [];
    });
    const [selectedSport, setSelectedSport] = useState(() => (
        sessionService.getLocalSetting('picksViewer_selectedSport', '')
    ));
    const [selectedMode, setSelectedMode] = useState(() => (
        sessionService.getLocalSetting('picksViewer_selectedMode', 'all')
    ));
    const [selectedGameBetTypes, setSelectedGameBetTypes] = useState(() => {
        const saved = sessionService.getLocalSetting('picksViewer_selectedGameBetTypes', []);
        return Array.isArray(saved) ? saved : [];
    });
    const [selectedPropMarketTypes, setSelectedPropMarketTypes] = useState(() => {
        const saved = sessionService.getLocalSetting('picksViewer_selectedPropMarketTypes', []);
        return Array.isArray(saved) ? saved : [];
    });
    const [selectedPropDirections, setSelectedPropDirections] = useState(() => {
        const saved = sessionService.getLocalSetting('picksViewer_selectedPropDirections', []);
        return Array.isArray(saved) ? saved : [];
    });
    const [selectedConfidenceLevels, setSelectedConfidenceLevels] = useState(() => {
        const saved = sessionService.getLocalSetting('picksViewer_selectedConfidenceLevels', []);
        return Array.isArray(saved) ? saved : [];
    });
    const [searchText, setSearchText] = useState(() => (
        sessionService.getLocalSetting('picksViewer_searchText', '')
    ));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isFilterModalClosing, setIsFilterModalClosing] = useState(false);
    const [expandedCardId, setExpandedCardId] = useState(null);
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [hasShownScrollSignupPrompt, setHasShownScrollSignupPrompt] = useState(false);
    const [savedBetIds, setSavedBetIds] = useState(() => new Set(
        betCartService.getAllBets().map((bet) => bet.id)
    ));
    const firstLockedCardRef = useRef(null);

    const selectedModeConfig = PICK_MODES.find((mode) => mode.key === selectedMode) || PICK_MODES[0];

    const optionBets = useMemo(() => {
        const items = [];
        optionEvents.forEach((event) => {
            (event.bets || []).forEach((bet) => {
                items.push({ event, bet });
            });
        });
        return items;
    }, [optionEvents]);

    const propMarketOptions = useMemo(() => {
        const counts = new Map();
        optionBets.forEach(({ bet }) => {
            if (bet.betType !== 'prop' || !bet.marketType) return;
            const current = counts.get(bet.marketType) || {
                type: bet.marketType,
                displayName: bet.marketDisplayName || bet.marketType,
                count: 0
            };
            current.count += 1;
            if (bet.marketDisplayName) current.displayName = bet.marketDisplayName;
            counts.set(bet.marketType, current);
        });
        return Array.from(counts.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [optionBets]);

    const gameBetTypeOptions = useMemo(() => {
        const counts = new Map();
        optionBets.forEach(({ bet }) => {
            if (!bet.betType || bet.betType === 'prop') return;
            const type = bet.betType;
            const current = counts.get(type) || {
                type,
                displayName: formatBetType(type),
                count: 0
            };
            current.count += 1;
            counts.set(type, current);
        });
        return Array.from(counts.values()).sort((a, b) => (
            (GAME_BET_TYPE_ORDER[a.type] || 99) - (GAME_BET_TYPE_ORDER[b.type] || 99) ||
            a.displayName.localeCompare(b.displayName)
        ));
    }, [optionBets]);

    const propDirectionOptions = useMemo(() => {
        const counts = new Map();
        optionBets.forEach(({ bet }) => {
            if (bet.betType !== 'prop' || !bet.direction) return;
            const type = String(bet.direction).toLowerCase();
            const current = counts.get(type) || {
                type,
                displayName: type.charAt(0).toUpperCase() + type.slice(1),
                count: 0
            };
            current.count += 1;
            counts.set(type, current);
        });
        return Array.from(counts.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [optionBets]);

    const confidenceOptions = useMemo(() => {
        const counts = new Map();
        optionBets.forEach(({ bet }) => {
            const confidence = normalizeFilterValue(getBetConfidence(bet)).toUpperCase();
            if (!confidence) return;
            const current = counts.get(confidence) || {
                type: confidence,
                displayName: confidence.charAt(0) + confidence.slice(1).toLowerCase(),
                count: 0
            };
            current.count += 1;
            counts.set(confidence, current);
        });
        const levelOrder = { HIGH: 1, MEDIUM: 2, LOW: 3 };
        return Array.from(counts.values()).sort((a, b) => (
            (levelOrder[a.type] || 99) - (levelOrder[b.type] || 99) || a.displayName.localeCompare(b.displayName)
        ));
    }, [optionBets]);

    const flattenedBets = useMemo(() => {
        const items = [];
        const query = searchText.trim().toLowerCase();
        events.forEach((event) => {
            (event.bets || []).forEach((bet) => {
                const confidence = normalizeFilterValue(getBetConfidence(bet)).toUpperCase();
                const matchesConfidence = selectedConfidenceLevels.length === 0 || selectedConfidenceLevels.includes(confidence);
                const searchableText = [
                    bet.label,
                    bet.recommendedBet,
                    bet.player,
                    bet.side,
                    bet.marketDisplayName,
                    bet.marketType,
                    event.eventName,
                    event.homeTeam,
                    event.awayTeam
                ].filter(Boolean).join(' ').toLowerCase();
                const matchesSearch = !query || searchableText.includes(query);

                if (!matchesConfidence || !matchesSearch) return;
                items.push({ event, bet });
            });
        });
        return items;
    }, [events, searchText, selectedConfidenceLevels]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_selectedSport', selectedSport);
    }, [selectedSport, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_selectedMode', selectedMode);
    }, [selectedMode, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_selectedGameBetTypes', selectedGameBetTypes);
    }, [selectedGameBetTypes, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_selectedPropMarketTypes', selectedPropMarketTypes);
    }, [selectedPropMarketTypes, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_selectedPropDirections', selectedPropDirections);
    }, [selectedPropDirections, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_selectedConfidenceLevels', selectedConfidenceLevels);
    }, [selectedConfidenceLevels, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_searchText', searchText);
    }, [searchText, sessionService]);

    useEffect(() => {
        sessionService.setLocalSetting('picksViewer_knownSports', knownSports);
    }, [knownSports, sessionService]);

    useEffect(() => {
        let isCancelled = false;

        const fetchOptionRecommendations = async () => {
            if (!canUsePicksFilters) {
                setOptionEvents([]);
                return;
            }

            try {
                const response = await getBetRecommendations(selectedSport ? { sport: selectedSport } : {});
                const results = response?.data?.results || {};
                const nextEvents = Array.isArray(results.events) ? results.events : [];
                if (isCancelled) return;

                setOptionEvents(nextEvents);
                const availablePropMarketTypes = new Set();
                const availablePropDirections = new Set();
                nextEvents.forEach((event) => {
                    (event.bets || []).forEach((bet) => {
                        if (bet.betType !== 'prop') return;
                        if (bet.marketType) availablePropMarketTypes.add(bet.marketType);
                        if (bet.direction) availablePropDirections.add(String(bet.direction).toLowerCase());
                    });
                });
                setSelectedPropMarketTypes((prev) => prev.filter((type) => availablePropMarketTypes.has(type)));
                setSelectedPropDirections((prev) => prev.filter((direction) => availablePropDirections.has(direction)));
                const responseSports = Array.from(new Set(nextEvents.map((event) => event.sport).filter(Boolean))).sort();
                if (responseSports.length > 0) {
                    setKnownSports((prev) => Array.from(new Set([...prev, ...responseSports])).sort());
                }
            } catch (fetchError) {
                if (!isCancelled) {
                    setOptionEvents([]);
                }
            }
        };

        fetchOptionRecommendations();

        return () => {
            isCancelled = true;
        };
    }, [canUsePicksFilters, selectedSport]);

    useEffect(() => {
        const updateSavedBetIds = () => {
            setSavedBetIds(new Set(betCartService.getAllBets().map((bet) => bet.id)));
        };

        eventBus.on('PicksViewer', EVENT_BET_CART_UPDATED, updateSavedBetIds);
        return () => eventBus.off('PicksViewer', EVENT_BET_CART_UPDATED);
    }, [betCartService]);

    useEffect(() => {
        let isCancelled = false;

        const fetchRecommendations = async () => {
            setLoading(true);
            setError('');
            setExpandedCardId(null);

            try {
                const requestBetTypes = canUsePicksFilters
                    ? (selectedMode === 'game' && selectedGameBetTypes.length > 0
                        ? selectedGameBetTypes
                        : selectedModeConfig.betTypes || [])
                    : [];
                const response = await getBetRecommendations(canUsePicksFilters ? {
                    sport: selectedSport,
                    betTypes: requestBetTypes,
                    propMarketTypes: selectedMode === 'game' ? [] : selectedPropMarketTypes,
                    propDirections: selectedMode === 'game' ? [] : selectedPropDirections
                } : {});
                const results = response?.data?.results || {};
                const nextEvents = Array.isArray(results.events) ? results.events : [];
                if (isCancelled) return;

                setEvents(nextEvents);
                setAccessMeta(results.access || null);
                setTotalBets(Number(results.totalBets || 0));
                const responseSports = Array.from(new Set(nextEvents.map((event) => event.sport).filter(Boolean))).sort();
                if (responseSports.length > 0) {
                    setKnownSports((prev) => Array.from(new Set([...prev, ...responseSports])).sort());
                }
            } catch (fetchError) {
                if (!isCancelled) {
                    setEvents([]);
                    setAccessMeta(null);
                    setTotalBets(0);
                    setError('Unable to load picks right now.');
                }
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };

        fetchRecommendations();

        return () => {
            isCancelled = true;
        };
    }, [selectedSport, selectedMode, selectedModeConfig.betTypes, selectedGameBetTypes, selectedPropMarketTypes, selectedPropDirections, canUsePicksFilters]);

    useEffect(() => {
        if (isAuthenticated || hasShownScrollSignupPrompt || !firstLockedCardRef.current) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                setShowRegistrationModal(true);
                setHasShownScrollSignupPrompt(true);
            },
            { threshold: 0.25 }
        );

        observer.observe(firstLockedCardRef.current);

        return () => observer.disconnect();
    }, [flattenedBets.length, isAuthenticated, hasShownScrollSignupPrompt]);

    const toggleCard = (cardId) => {
        setExpandedCardId((prev) => prev === cardId ? null : cardId);
    };

    const handleToggleSaved = (cartBet) => {
        const betId = betCartService.generateBetId(cartBet);

        if (!betId) return;

        if (betCartService.isBetSaved(betId)) {
            betCartService.removeBet(betId);
            return;
        }

        betCartService.addBet(cartBet);
    };

    const shouldBlurCard = (index) => {
        return !isAuthenticated && index >= LOGGED_OUT_VISIBLE_CARD_LIMIT;
    };

    const toggleGameBetType = (type) => {
        setSelectedGameBetTypes((prev) => (
            prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
        ));
    };

    const togglePropMarketType = (type) => {
        setSelectedPropMarketTypes((prev) => (
            prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
        ));
    };

    const togglePropDirection = (type) => {
        setSelectedPropDirections((prev) => (
            prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
        ));
    };

    const toggleConfidenceLevel = (type) => {
        setSelectedConfidenceLevels((prev) => (
            prev.includes(type) ? prev.filter((item) => item !== type) : [...prev, type]
        ));
    };

    const clearFilters = () => {
        setSelectedGameBetTypes([]);
        setSelectedPropMarketTypes([]);
        setSelectedPropDirections([]);
        setSelectedConfidenceLevels([]);
        setSearchText('');
    };

    const clearAllFilters = () => {
        clearFilters();
        setSelectedSport('');
        setSelectedMode('all');
    };

    const openFilterModal = () => {
        if (!canUsePicksFilters) {
            setShowUpgradeModal(true);
            return;
        }

        setIsFilterModalOpen(true);
        setIsFilterModalClosing(false);
    };

    const closeFilterModal = () => {
        if (!isFilterModalOpen || isFilterModalClosing) return;
        setIsFilterModalClosing(true);
        setTimeout(() => {
            setIsFilterModalOpen(false);
            setIsFilterModalClosing(false);
        }, 300);
    };

    const hasFilters = (
        selectedGameBetTypes.length > 0 ||
        selectedPropMarketTypes.length > 0 ||
        selectedPropDirections.length > 0 ||
        selectedConfidenceLevels.length > 0 ||
        searchText.trim()
    );
    const showGameFilters = selectedMode === 'game';
    const showPropFilters = selectedMode !== 'game';
    const activeFilterCount = (
        selectedGameBetTypes.length +
        selectedPropMarketTypes.length +
        selectedPropDirections.length +
        selectedConfidenceLevels.length +
        (searchText.trim() ? 1 : 0) +
        (selectedSport ? 1 : 0) +
        (selectedMode !== 'all' ? 1 : 0)
    );

    const header = (
        <div className={componentStyles.propsViewerHeader}>
            <div className={componentStyles.headerActions}>
                <button
                    type="button"
                    className={globalStyles.iconButton}
                    onClick={openFilterModal}
                >
                    <FaFilter />
                    <span>Filters</span>
                    {activeFilterCount > 0 && (
                        <span className={componentStyles.activeFilterCount}>{activeFilterCount}</span>
                    )}
                </button>
            </div>
        </div>
    );

    const filterModal = canUsePicksFilters && isFilterModalOpen ? (
        <div className={`${componentStyles.modalOverlay} ${isFilterModalClosing ? componentStyles.modalOverlayClosing : componentStyles.modalOverlayOpening}`} onClick={closeFilterModal}>
            <div className={`${componentStyles.filterModal} ${isFilterModalClosing ? componentStyles.filterModalClosing : componentStyles.filterModalOpening}`} onClick={(event) => event.stopPropagation()}>
                <div className={componentStyles.filterModalHeader}>
                    <h2>Bet Filters</h2>
                    <button
                        type="button"
                        className={`${globalStyles.iconButton} ${globalStyles.squareButton}`}
                        onClick={closeFilterModal}
                        aria-label="Close filters"
                    >
                        <FaTimes />
                    </button>
                </div>

                <div className={componentStyles.filterModalBody}>
                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Pick Type</div>
            <div className={componentStyles.filterRow}>
                {PICK_MODES.map((mode) => (
                    <button
                        key={mode.key}
                        type="button"
                        className={`${globalStyles.controlButton} ${selectedMode === mode.key ? globalStyles.activeControl : ''}`}
                        onClick={() => setSelectedMode(mode.key)}
                    >
                        {mode.label}
                    </button>
                ))}
            </div>
                    </section>

            {knownSports.length > 0 && (
                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Sports</div>
                <div className={componentStyles.filterRow}>
                    <button
                        type="button"
                        className={`${globalStyles.controlButton} ${selectedSport === '' ? globalStyles.activeControl : ''}`}
                        onClick={() => setSelectedSport('')}
                    >
                        All Sports
                    </button>
                    {knownSports.map((sport) => (
                        <button
                            key={sport}
                            type="button"
                            className={`${globalStyles.controlButton} ${selectedSport === sport ? globalStyles.activeControl : ''}`}
                            onClick={() => setSelectedSport(sport)}
                        >
                            {sport}
                        </button>
                    ))}
                </div>
                    </section>
            )}

            {showGameFilters && gameBetTypeOptions.length > 0 && (
                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Game Bets</div>
                <div className={componentStyles.filterRow}>
                    {gameBetTypeOptions.map((betType) => (
                        <button
                            key={betType.type}
                            type="button"
                            className={`${globalStyles.controlButton} ${selectedGameBetTypes.includes(betType.type) ? globalStyles.activeControl : ''}`}
                            onClick={() => toggleGameBetType(betType.type)}
                        >
                            {betType.displayName}
                            <span className={componentStyles.pillCount}>{betType.count}</span>
                        </button>
                    ))}
                </div>
                    </section>
            )}

            {showPropFilters && propMarketOptions.length > 0 && (
                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Prop Types</div>
                <div className={componentStyles.filterRow}>
                    {propMarketOptions.map((market) => (
                        <button
                            key={market.type}
                            type="button"
                            className={`${globalStyles.controlButton} ${selectedPropMarketTypes.includes(market.type) ? globalStyles.activeControl : ''}`}
                            onClick={() => togglePropMarketType(market.type)}
                        >
                            {market.displayName}
                            <span className={componentStyles.pillCount}>{market.count}</span>
                        </button>
                    ))}
                </div>
                    </section>
            )}

            {showPropFilters && propDirectionOptions.length > 0 && (
                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Direction</div>
                <div className={componentStyles.filterRow}>
                    {propDirectionOptions.map((direction) => (
                        <button
                            key={direction.type}
                            type="button"
                            className={`${globalStyles.controlButton} ${selectedPropDirections.includes(direction.type) ? globalStyles.activeControl : ''}`}
                            onClick={() => togglePropDirection(direction.type)}
                        >
                            {direction.displayName}
                            <span className={componentStyles.pillCount}>{direction.count}</span>
                        </button>
                    ))}
                </div>
                    </section>
            )}

            {confidenceOptions.length > 0 && (
                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Confidence</div>
                <div className={componentStyles.filterRow}>
                    {confidenceOptions.map((confidence) => (
                        <button
                            key={confidence.type}
                            type="button"
                            className={`${globalStyles.controlButton} ${selectedConfidenceLevels.includes(confidence.type) ? globalStyles.activeControl : ''}`}
                            onClick={() => toggleConfidenceLevel(confidence.type)}
                        >
                            {confidence.displayName}
                            <span className={componentStyles.pillCount}>{confidence.count}</span>
                        </button>
                    ))}
                </div>
                    </section>
            )}

                    <section className={componentStyles.filterSection}>
                        <div className={componentStyles.filterSectionTitle}>Search</div>
            <div className={componentStyles.searchRow}>
                <label className={componentStyles.searchBox} aria-label="Search picks">
                    <input
                        className={componentStyles.searchInput}
                        type="text"
                        placeholder="Search player, team, or matchup"
                        value={searchText}
                        onChange={(event) => setSearchText(event.target.value)}
                    />
                </label>
                <button
                    type="button"
                    className={`${globalStyles.iconButton} ${globalStyles.compactButton}`}
                    onClick={clearFilters}
                    disabled={!hasFilters}
                    title="Clear filters"
                >
                    <FaTimes />
                    <span>Clear</span>
                </button>
            </div>
                    </section>
                </div>

                <div className={componentStyles.filterModalFooter}>
                    <button
                        type="button"
                        className={globalStyles.secondaryButton}
                        onClick={clearAllFilters}
                        disabled={!hasFilters && !selectedSport && selectedMode === 'all'}
                    >
                        <FaTimes />
                        <span>Clear All</span>
                    </button>
                    <button
                        type="button"
                        className={globalStyles.primaryButton}
                        onClick={closeFilterModal}
                    >
                        Apply
                    </button>
                </div>
        </div>
        </div>
    ) : null;

    return (
        <>
            <PageView
                header={isAuthenticated ? header : null}
                containerClassName={componentStyles.propsViewerContainer}
                contentClassName={componentStyles.propsViewerContent}
            >
                {loading && <SmallLoadingAlt />}

                {error && (
                    <div className={componentStyles.emptyState}>{error}</div>
                )}

                {!loading && !error && flattenedBets.length === 0 && (
                    <div className={componentStyles.emptyState}>
                        No picks are available right now.
                    </div>
                )}

                {!loading && !error && flattenedBets.map(({ event, bet }, index) => {
                    const cardId = bet.id || [
                        event.eventId,
                        bet.betType,
                        bet.marketType,
                        bet.player || bet.side,
                        bet.recommendedBet
                    ].join('|');
                    const isBlurred = shouldBlurCard(index);
                    const isFirstLocked = !isAuthenticated && index === LOGGED_OUT_VISIBLE_CARD_LIMIT;
                    const cartBet = buildCartBetFromRecommendation(bet, event);
                    const cartBetId = betCartService.generateBetId(cartBet);

                    return (
                        <div
                            key={cardId}
                            ref={isFirstLocked ? firstLockedCardRef : null}
                            className={isBlurred ? componentStyles.blurredCard : ''}
                            style={isBlurred ? { filter: 'blur(4px)', pointerEvents: 'none' } : {}}
                        >
                            <PickCard
                                bet={bet}
                                event={event}
                                displayIndex={index + 1}
                                isExpanded={expandedCardId === cardId}
                                onToggle={() => toggleCard(cardId)}
                                isAuthenticated={isAuthenticated}
                                isSaved={savedBetIds.has(cartBetId)}
                                onToggleSaved={handleToggleSaved}
                            />
                        </div>
                    );
                })}

                {!accessMeta?.limited && !isAuthenticated && flattenedBets.length > LOGGED_OUT_VISIBLE_CARD_LIMIT && (
                    <div className={componentStyles.upgradePrompt}>
                        Sign up to see all {totalBets || flattenedBets.length} picks
                    </div>
                )}
            </PageView>
            {filterModal}
            <RegistrationModal
                isOpen={showRegistrationModal}
                onClose={() => setShowRegistrationModal(false)}
            />
            <RegistrationModal
                isOpen={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Upgrade to Pro"
                suggestionProps={{
                    title: 'Unlock Pick Filters',
                    subtitle: 'Upgrade to Pro to filter picks, search matchups, and refine the board.',
                    primaryLabel: 'View Pro Options',
                    primaryView: 'profile',
                    secondaryLabel: '',
                    secondaryView: '',
                    ctaContext: 'picks_filters_upgrade'
                }}
            />
        </>
    );
};

export default PropsViewer;
